/**
 * AssistantWelcome.tsx
 *
 * What the sidebar shows before the user has asked for anything. The page
 * summary used to be generated automatically on every page load and every
 * record click; it now runs only when the button here is pressed.
 *
 * Visual language deliberately matches the full-page assistant's EmptyState
 * (AIAssistantPage.tsx) so the two surfaces read as one feature.
 */
import { Sparkles, Loader2 } from 'lucide-react';
import type { ContextType, PageContextInfo } from '@/services/ai-context.service';
import { getSuggestions } from './suggestions';

interface Props {
  pageContext?: PageContextInfo | null;
  contextType?: ContextType | null;
  isSummarizing: boolean;
  onSummarize: () => void;
  onPick: (question: string) => void;
  disabled?: boolean;
}

export function AssistantWelcome({
  pageContext,
  contextType,
  isSummarizing,
  onSummarize,
  onPick,
  disabled,
}: Props) {
  const suggestions = getSuggestions(pageContext, contextType);

  const summarizeLabel = contextType ? `Summarize this ${contextType}` : 'Summarize this page';

  return (
    <div className="px-3 py-4">
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 shadow-sm">
          <Sparkles className="h-4 w-4 text-white" />
        </div>
        <p className="text-sm leading-relaxed text-slate-600">
          {contextType ? (
            <>
              Ask me anything about this{' '}
              <span className="font-semibold text-slate-800">{contextType}</span>.
            </>
          ) : pageContext?.pageName ? (
            <>
              Ask me anything about{' '}
              <span className="font-semibold text-slate-800">{pageContext.pageName}</span>.
            </>
          ) : (
            <>Ask me anything about what you&rsquo;re looking at.</>
          )}
        </p>
      </div>

      <button
        type="button"
        onClick={onSummarize}
        disabled={disabled || isSummarizing}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 px-3 py-2.5 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isSummarizing ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Analyzing&hellip;
          </>
        ) : (
          <>
            <Sparkles className="h-3.5 w-3.5" />
            {summarizeLabel}
          </>
        )}
      </button>

      <div className="mt-4 space-y-1.5">
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            disabled={disabled || isSummarizing}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-[13px] leading-snug text-slate-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
